import { X, RefreshCcw } from 'lucide-react'
import { DEFAULT_MODALITY_PROMPT } from '@/constant/modality-prompts'
import { Button } from '../ui/button'
type PromptModalProps = {
  open: boolean
  onClose: () => void
  sending: boolean
  onRun: (promptText: string) => void
}

export function PromptModal({ open, onClose, sending, onRun }: PromptModalProps) {
  if (!open) return null

  return (
    <>
      {/* Main Modal */}
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        {/* Backdrop */}
        <div className='absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity' onClick={() => !sending && onClose()} />

        {/* Modal */}
        <div className='relative z-10 w-full max-w-[1032px] h-auto max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden'>
          {/* Close button */}
          <button
            className='absolute top-6 right-6 w-8 h-8 rounded-full bg-transparent hover:bg-gray-100 flex items-center justify-center transition-colors z-10'
            onClick={onClose}
            disabled={sending}
            aria-label='Close modal'>
            <X className='w-5 h-5 text-gray-600' />
          </button>

          {/* Content */}
          <div className='p-12'>
            {/* Header */}
            <div className='mb-8'>
              <h2 className='text-3xl font-bold text-gray-900 mb-4'>Generate cluster map</h2>
              <p className='text-gray-600 text-sm leading-relaxed'>
                In order to map the active modality to cluster, click generate button and this will start the process. Once complete, you can view the modality
                to cluster association in Modality Manager.
              </p>
            </div>

            {/* System Prompt Section */}
            <div className='mb-8'>
              <div className='flex items-center gap-2 mb-4'>
                <label className='block text-lg font-semibold text-gray-900'>System prompt</label>
                {/*
                 className='w-4 h-4 text-gray-400' /> */}
              </div>
              <p className='text-gray-500 mb-4 text-sm'>A fixed prompt has been suggested, you can edit this prompt in next version.</p>

              <div className='relative'>
                <textarea
                  className='w-full p-4 text-sm text-gray-700 border border-gray-100 rounded-sm resize-none focus:ring-2 focus:border-transparent outline-none'
                  placeholder='You are an expert in learning modalities. Modality is a learning mode. Given the learning modalities defined in <examples> section, extract modalities identified in the <context> section.'
                  readOnly
                  aria-readonly='true'
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Generate Button - Centered */}
            <div className='flex justify-center'>
              <Button variant='primary' size='cta' onClick={() => onRun(DEFAULT_MODALITY_PROMPT)} disabled={sending} className='items-center justify-center'>
                <span>Generate cluster</span>
                <RefreshCcw className='w-4 h-4 shrink-0' aria-hidden='true' />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Popup */}
      {sending && (
        <div className='fixed inset-0 z-[60] flex items-center justify-center p-4'>
          <div className='absolute inset-0 bg-black/60 backdrop-blur-sm' />

          <div className='relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden'>
            <button
              onClick={onClose}
              disabled={sending}
              className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50'>
              <X className='w-6 h-6' />
            </button>

            <div className='p-16 text-center'>
              <h1 className='text-4xl font-bold text-gray-900 mb-8'>Generating cluster has begin</h1>

              <div className='w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-8'>
                <div className='w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin'></div>
              </div>

              <p className='text-lg text-gray-600 leading-relaxed max-w-lg mx-auto'>
                This will only take a few seconds, once complete, you can <strong>view</strong> the newly generated cluster in the{' '}
                <strong>cluster library</strong>.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
